import { type CountryCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useEffect, useRef, type SetStateAction } from 'react'
import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { type WpCartMemoryValues } from '../types'

type WpCartMemoryStore = Partial<Record<CountryCode, WpCartMemoryValues>>

export const defaultCartMemoryValues: WpCartMemoryValues = {
  payment: {
    creditCardAffiliateCode: null,
  },
}

const useWpCartMemoryStoreBase = create<WpCartMemoryStore>()(() => ({}))

export type SetWpCartMemoryValuesCallback = (state: WpCartMemoryValues) => void

export const useWpCartMemoryStore = (countryCode: CountryCode) => {
  const cartValues = useWpCartMemoryStoreBase(state => state[countryCode] || defaultCartMemoryValues)
  const callbacks = useRef<SetWpCartMemoryValuesCallback[]>([])

  const setState = useCallbackEvent((value: SetStateAction<WpCartMemoryValues>, callback?: SetWpCartMemoryValuesCallback) => {
    useWpCartMemoryStoreBase.setState((prevState) => {
      const nextState = typeof value === 'function' ? value(prevState[countryCode] || defaultCartMemoryValues) : value
      return {
        ...prevState,
        [countryCode]: nextState,
      }
    })
    if (callback) {
      callbacks.current.push(callback)
    }
  })

  useEffect(() => {
    if (callbacks.current) {
      callbacks.current.forEach(func => func(cartValues))

      callbacks.current = []
    }
  }, [cartValues])

  return [cartValues, setState] as const
}

export const useWpCartMemorySelector = <U>(countryCode: CountryCode, selector: (state: WpCartMemoryValues) => U) => {
  return useWpCartMemoryStoreBase(useShallow(state => selector(state[countryCode] || defaultCartMemoryValues)))
}
